import {Card, Skeleton} from 'antd';
import React, {FC, useEffect, useState, useRef, useCallback, MutableRefObject} from 'react';

import {ContentUnavailable} from 'components/content-unavailable';
import {DashboardContainer} from 'components/dashboard';
import {EditableTableContainer} from 'components/editable-table';
import {Spinner} from 'components/spinner';
import {UIBlocker} from 'components/ui-blocker';
import {
    selectMetadata, loadMetadata, ITableEntityMeta, IEntityMeta,
} from 'entities/metadata';
import {CONTAINER_SELECTORS} from 'features/horizontal-scroll/model/constants';
import {HorizontalScroll} from 'features/horizontal-scroll/ui/horizontal-scroll';
import {selectContextRawData, selectEntityNamesFetchedWithContext} from 'modules/context/context-selector';
import {selectEntityData, loadTableData} from 'modules/data';
import {initBlankFilter} from 'modules/data/actions/filter';
import {
    filterNonRenderableFieldsAndUpdateState as filterNonRenderableFieldsAndUpdateStateAction, resetLoadedData,
} from 'modules/data/data-actions';
import {selectIsEntityDataLoading, selectFilterEntityData} from 'modules/data/data-selectors';
import {TEntity, ITableEntityData, ITableStateEntityData} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {useForceUpdate} from 'shared/hooks/use-force-update';
import {TStateSetter} from 'shared/types/generics';
import {ISortQuery} from 'shared/types/requests';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    loadAcceptableExtensions,
    loadBranchNames,
    loadDocumentsSigningMethod,
    loadDocumentTypesOnUpload,
} from 'store/slices/documents-uploading-slice';
import {loadRegions} from 'store/slices/regions-slice';

import {ETableMode, TableModeContext} from './context';
import {useTableFeatures} from './hooks/use-table-features';
import {Table, ITableScroll} from './table';
import {TableActions} from './table-actions/table-actions';
import './table.less';

export interface ITableContainerProps {
    entityName: string;
    entityType?: EEntityType;
    url: string;
    hideTitle?: boolean;
    scroll?: ITableScroll;
    defaultSortQuery?: ISortQuery;
    overrideTableFilters?: TEntity;
}

export const TableContainer: FC<ITableContainerProps> = ({
    entityName,
    url,
    hideTitle,
    scroll,
    defaultSortQuery,
    overrideTableFilters,
}: ITableContainerProps) => {
    const dispatch = useAppDispatch();
    const forceUpdate = useForceUpdate();

    // own state
    const [tableMode, setTableMode] = useState<ETableMode>(ETableMode.VIEW);
    const [fieldsFiltered, setFieldsFiltered] = useState<boolean>(false);
    const [isMetaInitialized, setIsMetaInitialized] = useState(false);
    const [isErrorRequest, setIsErrorRequest] = useState(false);
    const tableRef = useRef<HTMLDivElement>(null);
    const [tableElement, setTableElement] = useState<HTMLDivElement | null>(null);

    // selectors
    const contextRawData = useAppSelector(selectContextRawData);
    const filter = useAppSelector(selectFilterEntityData(entityName));
    const loading = useAppSelector(selectIsEntityDataLoading(entityName));
    const data = (useAppSelector(selectEntityData(entityName, EEntityType.TABLE))) as ITableEntityData;
    const metadata = (useAppSelector(selectMetadata(entityName, EEntityType.TABLE))) as ITableEntityMeta;
    const metaDataFilter = (useAppSelector(selectMetadata(entityName, EEntityType.FILTER)));
    const entityNamesFetchedWithContext = useAppSelector(selectEntityNamesFetchedWithContext);
    const storedTableState = (useAppSelector(selectEntityData(entityName,
        EEntityType.TABLE_STATE))) as ITableStateEntityData;
    const wasFetchedWithContext = entityNamesFetchedWithContext.includes(entityName);

    const {handleTableChange, rowSelection} = useTableFeatures({
        entityName, url, metadata, data,
    });

    const handleTableRef = useCallback((node: HTMLDivElement | null) => {
        setTableElement(node);
        (tableRef as MutableRefObject<HTMLDivElement | null>).current = node;
    }, []);

    const filterNonRenderableFieldsAndUpdateState = (
        meta: IEntityMeta,
        name: string,
        entityType: EEntityType,
        setFilteredFlag: TStateSetter<boolean>,
    ) => dispatch(filterNonRenderableFieldsAndUpdateStateAction(meta, name, entityType, setFilteredFlag));

    const tableDataOptions = {
        useContext: metadata?.useContext,
        useSearch: metadata?.isSearchable,
        disabledPagination: metadata?.disabledPagination,
    };

    useEffect(() => (() => {
        dispatch(resetLoadedData(entityName, EEntityType.TABLE_STATE));
        dispatch(resetLoadedData(entityName, EEntityType.TABLE));
    }), [entityName]);

    useEffect(() => {
        dispatch(loadDocumentsSigningMethod());

        (async () => {
            if (!metadata) {
                const loadedMetadata = await dispatch(loadMetadata(entityName, EEntityType.TABLE));
                setIsMetaInitialized(true);
                if (loadedMetadata && loadedMetadata?.useContext && loadedMetadata?.prefix) {
                    await dispatch(loadRegions());
                    await dispatch(loadDocumentTypesOnUpload());
                    await dispatch(loadBranchNames());
                }
                setIsErrorRequest(!loadedMetadata);
            } else setIsMetaInitialized(true);
        })();
    }, []);

    useEffect(() => {
        if (contextRawData
          && metadata?.useContext
          && isMetaInitialized
          && !wasFetchedWithContext
        ) {
            dispatch(loadMetadata(entityName, EEntityType.TABLE, true));
        }
    }, [metadata?.useContext, contextRawData, isMetaInitialized]);

    useEffect(() => {
        const useContext = metadata?.useContext;
        const isFilterLoaded = (filter && (!filter?.loading || !metaDataFilter)) || !filter;
        const shouldLoadData = fieldsFiltered && (!data || !data?.rows);
        const shouldFilterData = !filter?.data && metadata?.isFilterable;

        const shouldLoadFilteredData = useContext
            ? contextRawData && shouldLoadData && !shouldFilterData
            : shouldLoadData && !shouldFilterData;
        if (shouldFilterData) dispatch(initBlankFilter(entityName));
        const paramsToBeConverted = {
            ...filter?.data,
            ...storedTableState?.data,
            ...overrideTableFilters,
        };

        if (shouldLoadFilteredData && isFilterLoaded) {
            dispatch(loadTableData(entityName, {
                ...tableDataOptions,
                paramsToBeConverted,
                params: defaultSortQuery ?? metadata?.defaultSort,
            }, url, metadata?.prefix, metadata?.urlConfig));
        }
    }, [filter, entityName, data, fieldsFiltered, contextRawData, storedTableState]);

    useEffect(() => {
        if (metadata?.shouldLoadExtensions) {
            dispatch(loadAcceptableExtensions());
        }
    }, [metadata?.shouldLoadExtensions]);

    useEffect(() => {
        if (!tableRef.current) {
            return () => {};
        }

        const resizeObserver = new ResizeObserver(() => {
            forceUpdate();
        });

        resizeObserver.observe(tableRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [tableElement]);

    useEffect(() => {
        if (metadata?.fields && !fieldsFiltered) {
            filterNonRenderableFieldsAndUpdateState(metadata, metadata.name, EEntityType.TABLE, setFieldsFiltered);
        }
    }, [metadata?.fields]);

    const isLoadedTable = metadata?.fields && fieldsFiltered && data?.rows;
    const cardTitle = !hideTitle && metadata?.title;

    const renderContent = () => {
        if (isErrorRequest) {
            return <></>;
        }
        if (!isLoadedTable) {
            return <Spinner />;
        }
        
        const tableContent = metadata?.isEditable ? (
            <EditableTableContainer
                metadata={metadata}
                tableData={data}
                url={url}
                rowSelection={rowSelection}
                onChange={handleTableChange}
                loading={loading}
                entityName={entityName}
                scroll={scroll}
            />
        ) : (
            <Table
                metadata={metadata}
                tableData={data}
                url={url}
                rowSelection={rowSelection}
                onChange={handleTableChange}
                loading={loading}
                entityName={entityName}
                scroll={scroll}
            />
        );
        
        return (
            <>
                <div ref={handleTableRef}>
                    {tableContent}
                </div>
                <HorizontalScroll
                    containerRef={tableRef}
                    scrollableSelector={CONTAINER_SELECTORS.TABLE}
                    enabled
                />
            </>
        )
    };
    return (
        <UIBlocker
            content={isErrorRequest && <ContentUnavailable />}
            opacity={0}
        >
            <Card
                title={cardTitle ?? (
                    <div>
                        <Skeleton
                            loading
                            round
                            title={false}
                            active
                            className="table-container__skeleton"
                            paragraph={{
                                rows: 1,
                                width: 140,
                                className: 'table-container__skeleton__paragraph',
                            }}
                        />
                    </div>
                )}
                className="table-container"
            >
                {metadata?.dashboardEntityName && (
                    <DashboardContainer
                        parentEntityName={entityName}
                        entityName={metadata?.dashboardEntityName}
                    />
                )}
                <TableModeContext.Provider value={{
                    tableMode,
                    setTableMode,
                    resetTableMode: () => {
                        if (tableMode === ETableMode.EDIT) {
                            setTableMode(ETableMode.VIEW);
                        }
                    },
                }}
                >
                    {!metadata?.isEditable && (
                        <TableActions
                            url={url}
                            entityName={entityName}
                            metadata={metadata}
                        />
                    )}
                    {renderContent()}
                </TableModeContext.Provider>
            </Card>
        </UIBlocker>
    );
};
